// ================================================================
// Telefonnummern — EINE Stelle, die aus «079 123 45 67» eine
// internationale Nummer macht.
// ================================================================
// In der Schweiz schreibt niemand die Vorwahl mit; Kunden diktieren und
// Portale liefern «079 …», «0041 79 …» oder «+41 79 …» bunt gemischt. Ein
// WhatsApp-Link braucht aber die Nummer OHNE Plus und MIT Landesvorwahl, sonst
// öffnet WhatsApp den Chat nicht — der Knopf tat einfach nichts.
//
// Warum hier und nicht in jedem Aufrufer: genau daran ist es schiefgegangen.
// Der Server normalisierte längst korrekt (voice.services.ts), der WhatsApp-
// Link im Browser warf dagegen nur alles weg, was keine Ziffer ist. Zwei
// Rechenwege für dieselbe Frage — einer davon falsch.
//
// (Backend und Frontend können sich keinen Code teilen; die REGEL steht
// deshalb an beiden Enden bewusst gleich, Zeile für Zeile nachvollziehbar.)

import { toAsciiDigits } from './digits.js';

/**
 * Landesvorwahl ohne «+», die gilt, wenn die Nummer keine eigene mitbringt.
 * Bewusst eine Konstante an EINER Stelle: wird daraus eines Tages eine
 * Firmen-Einstellung («unser Markt ist Österreich»), ist das hier ein Feld
 * statt einer Suche quer durch die App.
 */
export const DEFAULT_DIAL_CODE = '41'; // Schweiz

/**
 * Nummer → nur Ziffern, international, ohne «+».
 *
 * - «079 123 45 67»   → «41791234567»  (führende 0 ist die nationale
 *                                       Verkehrsziffer und ENTFÄLLT)
 * - «+41 79 123 45 67»→ «41791234567»  (unverändert)
 * - «0041 79 …»       → «41791234567»  (internationales Präfix 00)
 * - «+49 170 …»       → «49170…»       (anderes Land bleibt, wie es ist)
 *
 * Leere/unbrauchbare Eingabe → leerer String; der Aufrufer entscheidet, ob er
 * damit einen Link baut. Eine erfundene Nummer wäre schlimmer als kein Link.
 */
export function toInternationalPhone(phone, dialCode = DEFAULT_DIAL_CODE) {
  // toAsciiDigits ZUERST: eine persisch gespeicherte «۰۷۹…» bliebe sonst als
  // leerer String übrig (utils/digits.js).
  let raw = toAsciiDigits(String(phone || '')).trim();
  if (!raw) return '';
  // «+41 (0)79 …» ist auf Schweizer Briefköpfen und in Signaturen üblich: die
  // Null in Klammern gilt NUR beim Wählen im Inland und gehört nicht in die
  // internationale Form. Sie muss weg, BEVOR die Ziffern zusammengezogen
  // werden — sonst entsteht «410 79…» und WhatsApp findet niemanden.
  raw = raw.replace(/\(\s*0\s*\)/g, '');
  // «+» merken, BEVOR er weggefiltert wird: er sagt «Landesvorwahl steht schon da».
  const hadPlus = raw.startsWith('+');
  let digits = raw.replace(/\D+/g, '');
  if (!digits) return '';

  if (hadPlus) return digits;
  // «00» ist das internationale Präfix — dahinter steht bereits ein Land.
  if (digits.startsWith('00')) return digits.slice(2);
  // Beginnt sie schon mit unserer Vorwahl, nichts doppeln. Achtung: das ist
  // eine Heuristik. Eine Schweizer Nummer ohne 0 («41 79 …» als Tippfehler)
  // ist von «+41…» nicht unterscheidbar — beide führen zum selben Ergebnis,
  // solange die Vorwahl unsere eigene ist.
  if (digits.startsWith(dialCode)) return digits;
  // Nationale Schreibweise: führende Null(en) weg, Vorwahl davor.
  digits = digits.replace(/^0+/, '');
  return digits ? dialCode + digits : '';
}
